/**
 * Utility functions for compressing signature images to reduce file size
 */

/**
 * Compress a signature data URL to reduce file size
 * @param dataUrl - The original signature data URL
 * @param options - Compression options
 * @returns Promise<string> - Compressed data URL
 */
export const compressSignature = (
  dataUrl: string,
  options: {
    format?: 'image/jpeg' | 'image/png' | 'image/webp';
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
  } = {},
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const {
      format = 'image/jpeg',
      quality = 0.6,
      maxWidth = 400,
      maxHeight = 200,
    } = options;

    const img = new Image();
    img.onload = () => {
      try {
        // Create a canvas to resize and compress the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspectRatio;
        }

        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress the image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to compressed data URL
        const compressedDataUrl = canvas.toDataURL(format, quality);
        resolve(compressedDataUrl);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load signature image'));
    };

    img.src = dataUrl;
  });
};

/**
 * Get optimal compression settings for different use cases
 */
export const getCompressionSettings = (
  useCase: 'preview' | 'pdf' | 'storage' = 'pdf',
) => {
  switch (useCase) {
    case 'preview':
      return {
        format: 'image/jpeg' as const,
        quality: 0.8,
        maxWidth: 300,
        maxHeight: 150,
      };
    case 'pdf':
      return {
        format: 'image/jpeg' as const,
        quality: 0.6,
        maxWidth: 400,
        maxHeight: 200,
      };
    case 'storage':
      return {
        format: 'image/jpeg' as const,
        quality: 0.5,
        maxWidth: 300,
        maxHeight: 150,
      };
    default:
      return {
        format: 'image/jpeg' as const,
        quality: 0.6,
        maxWidth: 400,
        maxHeight: 200,
      };
  }
};

/**
 * Estimate the size reduction of a compressed signature
 * @param originalDataUrl - The original signature data URL
 * @param compressedDataUrl - The compressed signature data URL
 * @returns number - Size reduction percentage
 */
export const getSizeReduction = (
  originalDataUrl: string,
  compressedDataUrl: string,
): number => {
  const originalSize = originalDataUrl.length;
  const compressedSize = compressedDataUrl.length;
  const reduction = ((originalSize - compressedSize) / originalSize) * 100;
  return Math.round(reduction);
};
